import { useMemo } from "react";
import { IUserReaction } from "src/components/user-reactions/types";
import { TAggregatedReactions } from "src/types/common";
import { EnumReactionType } from "src/types/common-enum";

type TReactionWeights = { [key: string]: number };
const REACTION_WEIGHTS: TReactionWeights = {
    "1": 0.5,
    "2": 1,
    "3": 1.5,
    "4": 2.5,
    "5": 3,
};

const UNDERLYING_REACTION_WEIGHTS: TReactionWeights = {
    "-2": 3,
    "-1": 2,
    "0": 1,
    "1": 2,
    "2": 3,
};

const reducePositiveReactions = (reaction: IUserReaction, weights?: TReactionWeights) =>
    reaction.reactionValues.reduce(
        (acc, reactionValue) =>
            reactionValue.value > 0 ? acc + reactionValue.numberOfReactions * (weights ? weights[reactionValue.value] : 1) : acc,
        0
    );

const reduceNegativeReactions = (reaction: IUserReaction, weights?: TReactionWeights) =>
    reaction.reactionValues.reduce(
        (acc, reactionValue) =>
            reactionValue.value < 0 ? acc + reactionValue.numberOfReactions * (weights ? weights[reactionValue.value] : 1) : acc,
        0
    );

const reduceReactions = (reaction: IUserReaction, weights?: TReactionWeights) =>
    reaction.reactionValues.reduce((acc, reactionValue) => acc + reactionValue.numberOfReactions * (weights ? weights[reactionValue.value] : 1), 0);

const mapReactionsWithConfigToAggregatedReactions = (reactions: ReadonlyArray<IUserReaction>): TAggregatedReactions => {
    const mappedReactions = reactions.map(reaction => {
        const isUnderlyingReaction = reaction.reactionType === EnumReactionType.ArticleUserPricePredictionUnderlying;
        return {
            reactionType: reaction.reactionType,
            numberOfReactions: reduceReactions(reaction),
            positiveReactions: isUnderlyingReaction ? reducePositiveReactions(reaction) : 0,
            negativeReactions: isUnderlyingReaction ? reduceNegativeReactions(reaction) : 0,
            weightedPositiveReactions: isUnderlyingReaction ? reducePositiveReactions(reaction, UNDERLYING_REACTION_WEIGHTS) : 0,
            weightedNegativeReactions: isUnderlyingReaction ? reduceNegativeReactions(reaction, UNDERLYING_REACTION_WEIGHTS) : 0,
            weightedNumberOfReactions: isUnderlyingReaction
                ? reduceReactions(reaction, UNDERLYING_REACTION_WEIGHTS)
                : reduceReactions(reaction, REACTION_WEIGHTS),
        };
    });

    const standardReactions = mappedReactions.filter(reaction => reaction.reactionType !== EnumReactionType.ArticleUserPricePredictionUnderlying);
    const combinedUnderlyingReactions = mappedReactions
        .filter(reaction => reaction.reactionType === EnumReactionType.ArticleUserPricePredictionUnderlying)
        .reduce(
            (acc, reaction) => {
                return {
                    reactionType: EnumReactionType.ArticleUserPricePredictionUnderlying,
                    numberOfReactions: acc.numberOfReactions + reaction.numberOfReactions,
                    positiveReactions: acc.positiveReactions + reaction.positiveReactions,
                    negativeReactions: acc.negativeReactions + reaction.negativeReactions,
                    weightedNegativeReactions: acc.weightedNegativeReactions + reaction.weightedNegativeReactions,
                    weightedPositiveReactions: acc.weightedPositiveReactions + reaction.weightedPositiveReactions,
                    weightedNumberOfReactions: acc.weightedNumberOfReactions + reaction.weightedNumberOfReactions,
                };
            },
            {
                reactionType: EnumReactionType.ArticleUserPricePredictionUnderlying,
                numberOfReactions: 0,
                positiveReactions: 0,
                negativeReactions: 0,
                weightedNumberOfReactions: 0,
                weightedPositiveReactions: 0,
                weightedNegativeReactions: 0,
            }
        );
    return [...standardReactions, combinedUnderlyingReactions];
};

export const useNewsUserAggregatedReactions = (reactions: ReadonlyArray<IUserReaction>) => {
    return useMemo(() => mapReactionsWithConfigToAggregatedReactions(reactions), [reactions]);
};
